import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, runSupabaseWakeUp } from '../lib/supabase';
import { normalizeRole } from '../lib/roles';
import { logActivity } from '../lib/activityLog';
import { sendRecoveryEmail } from '../lib/sendAppEmail';
import {
  shouldSendLoginNotificationEmail,
  sendLoginNotificationEmail,
  sendPasswordChangedEmail,
} from '../services/emailService';

/** Shown after sign-out when profile.active is false. Login reads from sessionStorage key below. */
export const INACTIVE_ACCOUNT_MESSAGE =
  'Your account has been deactivated. Contact your administrator if you need access.';
export const INACTIVE_NOTICE_STORAGE_KEY = 'ecdt_inactive_notice';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cachedRole, setCachedRole] = useState(() => {
    try {
      return localStorage.getItem('ecdt_role_cache') || '';
    } catch {
      return '';
    }
  });
  const logoutInProgressRef = React.useRef(false);
  const isMountedRef = React.useRef(true);

  /** Sign-in / sign-up can be slow on cold Supabase; avoid false timeouts. */
  const FETCH_TIMEOUT_MS = 35000;
  function withTimeout(promise, timeoutMs = FETCH_TIMEOUT_MS) {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), timeoutMs)
      ),
    ]);
  }

  async function lockoutInactiveAccount() {
    try {
      sessionStorage.setItem(INACTIVE_NOTICE_STORAGE_KEY, INACTIVE_ACCOUNT_MESSAGE);
    } catch {
      // ignore
    }
    logoutInProgressRef.current = true;
    setUser(null);
    setProfile(null);
    setCachedRole('');
    try {
      localStorage.removeItem('ecdt_role_cache');
    } catch {
      // ignore
    }
    setLoading(false);
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (e) {
      console.error('[AuthContext] signOut after inactive account', e);
    } finally {
      logoutInProgressRef.current = false;
    }
  }

  async function fetchProfile(userId) {
    const { data, error } = await Promise.race([
      supabase.from('profiles').select('*').eq('id', userId).single(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('profile_timeout')), FETCH_TIMEOUT_MS)
      ),
    ]).catch((e) => ({ data: null, error: e }));
    if (!isMountedRef.current) return;
    if (!error && data) {
      if (data.active === false) {
        await lockoutInactiveAccount();
        return;
      }
      setProfile(data);
      const roleFromProfile = normalizeRole(data?.role ?? data?.user_role);
      setCachedRole(roleFromProfile);
      try {
        localStorage.setItem('ecdt_role_cache', roleFromProfile);
      } catch {
        // ignore storage failures
      }
      return;
    }
    console.warn('[AuthContext] fetchProfile failed; keeping cached role/profile', error?.message || error);
  }

  useEffect(() => {
    let isMounted = true;
    isMountedRef.current = true;

    // Failsafe: never leave the app stuck on "Loading…" forever
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
      }
    }, 5000);

    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (!isMounted) return;
        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchProfile(session.user.id);
        }
        if (isMounted) setLoading(false);
      })
      .catch((error) => {
        console.error('Error getting Supabase session', error);
        if (isMounted) {
          setLoading(false);
        }
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // IMPORTANT (Supabase docs): callback must not be async.
      // Avoid deadlocks by not awaiting other Supabase APIs inside this callback.
      if (!isMounted) return;
      console.log('[AuthContext] onAuthStateChange', {
        event,
        hasSession: !!session,
        userId: session?.user?.id ?? null,
        visibilityState: document.visibilityState,
        logoutInProgress: logoutInProgressRef.current,
      });

      setUser(session?.user ?? null);

      if (session?.user) {
        // Fetch profile asynchronously, but do not await inside onAuthStateChange callback.
        void fetchProfile(session.user.id);
      } else {
        setProfile(null);
      }

      setLoading(false);
      if (!session?.user) logoutInProgressRef.current = false;
    });

    return () => {
      isMounted = false;
      isMountedRef.current = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchProfile is defined in this component body before this effect
  }, []);

  /* When user returns to the tab, refresh session and wake up the Supabase data connection.
   * Browsers can throttle/suspend background tabs; the first request after focus may hang unless
   * we run a lightweight query here so subsequent navigations and saves work. */
  const userIdRef = React.useRef(user?.id);
  userIdRef.current = user?.id;
  useEffect(() => {
    async function onVisible() {
      if (document.visibilityState !== 'visible') return;
      console.log('[TabVisibility] AuthContext onVisible', {
        logoutInProgress: logoutInProgressRef.current,
        userIdRef: userIdRef.current,
      });

      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        const newId = session?.user?.id ?? null;
        const currentId = userIdRef.current;
        console.log('[TabVisibility] AuthContext getSession result', {
          newUserId: newId,
          currentUserId: currentId,
          same: newId === currentId,
          error: error?.message,
          logoutInProgress: logoutInProgressRef.current,
        });

        // If logout is in progress, don't let a just-arrived tab focus refresh repopulate user/profile.
        if (logoutInProgressRef.current) {
          console.log('[TabVisibility] skip setUser/setProfile due to logoutInProgress');
        } else if (newId !== currentId) {
          setUser(session?.user ?? null);
          if (session?.user) {
            void fetchProfile(session.user.id);
          } else {
            setProfile(null);
          }
        } else if (session?.user) {
          void fetchProfile(session.user.id);
        }

        /* Wake up PostgREST connection so subsequent requests (e.g. Contacts load, Department save) don't hang */
        supabase.from('profiles').select('id').limit(1).then(() => {
          console.log('[TabVisibility] Supabase wake-up query done');
        }).catch((e) => {
          console.warn('[TabVisibility] wake-up query failed:', e?.message || e);
        });
      } catch (e) {
        console.error('[TabVisibility] AuthContext onVisible error', e);
      }
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- fetchProfile is stable logic; re-subscribing on each render is wasteful */
  }, []);

  async function signUp({ email, password, fullName }) {
    await runSupabaseWakeUp().catch(() => {});
    const { data, error } = await withTimeout(
      supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName || undefined } },
      })
    );
    if (error) throw error;
    return data;
  }

  async function signIn({ email, password }) {
    await runSupabaseWakeUp().catch(() => {});
    const { data, error } = await withTimeout(
      supabase.auth.signInWithPassword({
        email,
        password,
      })
    );
    if (error) throw error;
    if (data?.user) {
      const { data: activeRow } = await supabase
        .from('profiles')
        .select('active')
        .eq('id', data.user.id)
        .maybeSingle();
      if (activeRow && activeRow.active === false) {
        await lockoutInactiveAccount();
        throw new Error(INACTIVE_ACCOUNT_MESSAGE);
      }
      await logActivity(supabase, data.user.id, {
        action: 'login',
        entity_type: 'auth',
        title: data.user.email || 'Signed in',
      });
      const userEmail = data.user.email;
      if (userEmail && shouldSendLoginNotificationEmail()) {
        sendLoginNotificationEmail({
          to: userEmail,
          displayName: data.user.user_metadata?.full_name,
        }).catch((err) => console.warn('[Auth] Login notification email:', err?.message || err));
      }
    }
    return data;
  }

  async function resetPasswordForEmail(email) {
    await runSupabaseWakeUp().catch(() => {});
    await sendRecoveryEmail({ email: email.trim() });
    return {};
  }

  async function updatePassword(newPassword) {
    await runSupabaseWakeUp().catch(() => {});
    const { data, error } = await withTimeout(supabase.auth.updateUser({ password: newPassword }));
    if (error) throw error;
    const addr = data?.user?.email || user?.email;
    if (addr) {
      sendPasswordChangedEmail({
        to: addr,
        displayName: data?.user?.user_metadata?.full_name || profile?.full_name,
      }).catch((err) => console.warn('[Auth] Password-changed email:', err?.message || err));
    }
    return data;
  }

  async function signOut() {
    const userId = user?.id;
    const userEmail = user?.email;
    // Immediately clear local auth state so the UI navigates back to Login
    console.log('[AuthContext] signOut called', {
      userId,
      userEmail,
      visibilityState: document.visibilityState,
      logoutInProgressBefore: logoutInProgressRef.current,
      loading,
    });
    logoutInProgressRef.current = true;

    setUser(null);
    setProfile(null);
    setCachedRole('');
    try {
      localStorage.removeItem('ecdt_role_cache');
    } catch {
      // ignore storage failures
    }
    setLoading(false);
    try {
      if (userId) {
        // Fire-and-forget activity log; don't block logout UI on this
        logActivity(supabase, userId, {
          action: 'logout',
          entity_type: 'auth',
          title: userEmail || 'Signed out',
        }).catch(() => {});
      }
      await runSupabaseWakeUp().catch(() => {});

      console.log('[AuthContext] signOut: calling supabase.auth.signOut({ scope: "local" })');
      const signOutPromise = supabase.auth.signOut({ scope: 'local' });
      const pendingWarnId = setTimeout(() => {
        console.warn('[AuthContext] signOut still pending after 12s');
      }, FETCH_TIMEOUT_MS);

      try {
        await withTimeout(signOutPromise, FETCH_TIMEOUT_MS);
        console.log('[AuthContext] supabase.auth.signOut resolved');
      } catch (err) {
        console.error('[AuthContext] Supabase signOut error (or timeout):', err);
      } finally {
        clearTimeout(pendingWarnId);
      }
    } catch (err) {
      console.error('Sign out error:', err);
    } finally {
      logoutInProgressRef.current = false;
    }
  }

  async function updateProfile(updates) {
    if (!user) return;
    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', user.id)
      .select()
      .single();
    if (error) throw error;
    setProfile(data);
    return data;
  }

  async function uploadAvatar(file) {
    if (!user) return null;
    const ext = file.name.split('.').pop();
    const path = `${user.id}/avatar.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true });
    if (uploadError) throw uploadError;
    const {
      data: { publicUrl },
    } = supabase.storage.from('avatars').getPublicUrl(path);
    await updateProfile({ avatar_url: publicUrl });
    return publicUrl;
  }

  const role = normalizeRole(profile?.role ?? profile?.user_role ?? user?.user_metadata?.role ?? cachedRole);

  const value = {
    user,
    profile,
    role,
    loading,
    signUp,
    signIn,
    signOut,
    resetPasswordForEmail,
    updatePassword,
    updateProfile,
    uploadAvatar,
    refreshProfile: () => user && fetchProfile(user.id),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
